import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { AdminUser } from '../types/auth';
import { adminApi } from '../services/adminApi';
import { ADMIN_TOKEN_STORAGE_KEY } from '../services/apiClient';

interface AdminAuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const ADMIN_AUTH_STORAGE_KEY = 'sparkadmin_auth';

interface StoredAuth {
  user: AdminUser;
  token: string;
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Load auth from localStorage and validate token on mount
   */
  useEffect(() => {
    const loadAuth = async () => {
      try {
        const storedAuth = localStorage.getItem(ADMIN_AUTH_STORAGE_KEY);
        const storedToken = localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);

        if (storedAuth && storedToken) {
          // Validate token by fetching profile
          try {
            const profileResponse = await adminApi.getProfile(storedToken);
            setUser(profileResponse.data);
          } catch (error) {
            // Token invalid or expired, clear storage
            console.error('Token validation failed:', error);
            localStorage.removeItem(ADMIN_AUTH_STORAGE_KEY);
            localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Failed to load admin auth from storage:', error);
        localStorage.removeItem(ADMIN_AUTH_STORAGE_KEY);
        localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    loadAuth();
  }, []);

  /**
   * Login with backend API
   */
  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await adminApi.login({ email, password });

      const { token, admin } = response.data;

      // Set user state
      setUser(admin);

      // Persist to localStorage
      const authData: StoredAuth = {
        user: admin,
        token,
      };
      localStorage.setItem(ADMIN_AUTH_STORAGE_KEY, JSON.stringify(authData));
      localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed',
      };
    }
  };

  /**
   * Logout and clear authentication
   */
  const logout = () => {
    setUser(null);
    localStorage.removeItem(ADMIN_AUTH_STORAGE_KEY);
    localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
  };

  const value: AdminAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

/**
 * Hook to access admin auth context
 * @throws Error if used outside AdminAuthProvider
 */
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}

/**
 * Get stored JWT token for API calls
 * @deprecated Import getAdminToken from apiClient instead
 * @returns JWT token or null if not authenticated
 */
export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
}
